import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Container } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { Eyebrow, HighlightedText, ArrowLink } from "blog/components/ui.js";
import { ReactComponent as EyeIcon } from "feather-icons/dist/icons/eye.svg";
import { ReactComponent as BookOpenIcon } from "feather-icons/dist/icons/book-open.svg";
import { ReactComponent as GitBranchIcon } from "feather-icons/dist/icons/git-branch.svg";
import { ReactComponent as ArrowRightIcon } from "feather-icons/dist/icons/arrow-right.svg";
import { ReactComponent as SvgDecoratorBlob3 } from "images/svg-decorator-blob-3.svg";
import designIllustration from "images/design-illustration-2.svg";

/**
 * Section « Notre ligne éditoriale » de la page d’accueil.
 * Trois verbes (Observer, Expliquer, Relier) pour dire ce que fait le média,
 * sans citer d’institution ni de chiffre (les acteurs locaux arrivent dans la
 * section suivante).
 *
 * Mise en page deux colonnes sur le modèle de
 * components/features/TwoColWithTwoFeaturesAndButtons.js, illustration à GAUCHE :
 * elle casse la suite de blocs centrés (Derniers articles, Acteurs clés) et alterne
 * avec le hero (image à droite) et « Se former » (image à droite). Les trois verbes
 * sont une liste de « features » à icône ronde, titre en h3.
 *
 * Le lien vers /about est masqué par défaut (le hero propose déjà « Découvrir le
 * projet » et la section « L’équipe » suit) ; passer `ctaText` pour l’afficher.
 */

/* Mise en page (TwoColumn porte déjà le rythme vertical py-20 md:py-24, comme TwoColWithSteps). */
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24 items-center`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0`;
const TextColumn = tw(Column)`md:w-7/12 mt-16 md:mt-0 md:ml-12 lg:ml-16`;
/* design-illustration-2.svg : 1130 × 813 (espace réservé avant chargement). */
const Illustration = tw.img`w-full`;

const TextContent = tw.div`lg:py-8 text-center md:text-left`;
const Subheading = tw(Eyebrow)`block`;
const Heading = tw(SectionHeading)`mt-4 font-black text-3xl sm:text-4xl text-center md:text-left leading-tight`;
/* secondary-300 : même bleu nuit que la DA, mais contraste AA sur fond blanc. */
const Description = tw(SectionDescription)`mt-4 mx-auto md:mx-0 text-secondary-300`;

/* Trois verbes en liste à icône */
const Features = tw.ul`mt-8 max-w-sm mx-auto md:mx-0 list-none`;
const Feature = tw.li`mt-8 flex items-start flex-col md:flex-row`;
const IconWrap = styled.div`
  ${tw`mx-auto inline-block border border-primary-500 text-center rounded-full p-2 flex-shrink-0`}
  svg {
    ${tw`w-5 h-5 text-primary-500`}
  }
`;
const FeatureText = tw.div`mt-4 md:mt-0 md:ml-4 text-center md:text-left`;
const Title = tw.h3`font-bold text-lg text-primary-500`;
/* Même gris que le texte des PostCards de « Derniers articles ». */
const Text = tw.p`mt-1 text-sm font-medium text-gray-600 leading-relaxed`;

/* Lien optionnel sous la liste (anneau de focus ajouté : ArrowLink n’a qu’un soulignement). */
const CtaRow = tw.div`mt-10 flex justify-center md:justify-start`;
const Cta = tw(ArrowLink)`rounded focus:shadow-outline`;

/* Décor : blob rose (fill codé en dur dans blob-3) en haut à DROITE, là où le bas de
   « Derniers articles » est vide (son blob violet est en bas à gauche). */
const DecoratorBlob = tw(
  SvgDecoratorBlob3
)`pointer-events-none absolute right-0 top-0 w-64 h-64 opacity-25 transform translate-x-32 -translate-y-24 -z-10`;

const defaultSteps = [
  {
    icon: EyeIcon,
    heading: "Observer",
    description:
      "Nous suivons ce qui se passe autour de Besançon : laboratoires, écoles, entreprises, hôpital, collectivités, salons. Chaque article s’appuie sur des sources citées.",
  },
  {
    icon: BookOpenIcon,
    heading: "Expliquer",
    description:
      "L’IA et les microtechniques sont des sujets exigeants. Nous les rendons lisibles pour les curieux, les étudiants, les entrepreneurs et les décideurs, sans simplifier à outrance.",
  },
  {
    icon: GitBranchIcon,
    heading: "Relier",
    description:
      "À Besançon, la précision est une tradition et l’écosystème reste à taille humaine : nos articles font le lien entre les personnes, les projets et les lieux.",
  },
];

export default function LigneEditoriale({
  subheading = "Notre ligne éditoriale",
  heading = (
    <>
      Observer, expliquer, relier&nbsp;: la tech d’ici, <HighlightedText>sans jargon.</HighlightedText>
    </>
  ),
  description = "Un média local, écrit pour les curieux comme pour les professionnels : ce que nous faisons, en trois verbes.",
  steps = defaultSteps,
  /* Vide par défaut : « Découvrir le projet » (hero) mène déjà à /about. */
  ctaText = "",
  ctaUrl = "/about",
  imageSrc = designIllustration,
}) {
  return (
    <Container id="ligne-editoriale">
      <TwoColumn>
        <ImageColumn>
          <Illustration src={imageSrc} alt="" width="1130" height="813" loading="lazy" />
        </ImageColumn>
        <TextColumn>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}

            <Features role="list">
              {steps.map(step => {
                const Icon = step.icon || EyeIcon;
                return (
                  <Feature key={step.heading}>
                    <IconWrap>
                      <Icon aria-hidden="true" focusable="false" />
                    </IconWrap>
                    <FeatureText>
                      <Title>{step.heading}</Title>
                      <Text>{step.description}</Text>
                    </FeatureText>
                  </Feature>
                );
              })}
            </Features>

            {ctaText && ctaUrl && (
              <CtaRow>
                <Cta to={ctaUrl}>
                  {ctaText}
                  <ArrowRightIcon aria-hidden="true" focusable="false" />
                </Cta>
              </CtaRow>
            )}
          </TextContent>
        </TextColumn>
      </TwoColumn>
      <DecoratorBlob aria-hidden="true" focusable="false" />
    </Container>
  );
}
